// InventoryPage — full warehouse visibility for the back office.
//
// Visual language: Satori-style table + KPI strip + side panel detail
// (slide-out from the right). Hot path is the "Receive delivery" modal,
// triggered from the header — owner-only because it touches cost.
//
// Cost gating: per the dashboard discovery rule, back-office cannot see
// margin/cost. We enforce this both server-side (the GET endpoints null
// out costCents/unitCostCents for non-owner callers) AND in the UI
// (cost column hidden, receive modal hidden, cost-bearing txn rows
// show "—" for the unit cost). The dev auth bypass returns owner role
// locally, so during local testing you'll see the full owner view.

import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  Fragment,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { isBlockingError } from '../lib/isBlockingError.js';
import {
  Search,
  PackagePlus,
  Upload,
  Plus,
  ChevronDown,
  Star,
  Check,
  X,
} from 'lucide-react';
import { useSortable, type SortState } from '../hooks/useSortable.js';
import { SortHeader } from '../components/SortHeader.js';
import { ColumnFilter } from '../components/ColumnFilter.js';
import { distinctValues, rowPasses, anyActive } from '../lib/column-filter.js';
import { InventoryDetailPanel } from '../components/InventoryDetailPanel.js';
import { CategoryManagerDialog } from '../components/CategoryManagerDialog.js';
import { ReceiveDeliveryDialog } from '../components/ReceiveDeliveryDialog.js';
import { NewSkuDialog } from '../components/NewSkuDialog.js';
import { CsvImport } from '../components/CsvImport.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog.js';
import {
  getInventorySkus,
  getInventoryTxns,
  getSkuCommitments,
  getSkuCategories,
  getMe,
  getSkus,
  patchSku,
  bulkPatchSkusActive,
  patchSkuCategory,
} from '../api/client.js';
import type {
  InventoryListResponse,
  InventorySkuRow,
  Sku,
  SkuCategory,
} from '@gutterinv/shared';
import { unitLabel, normalizeUom, computeSellPriceCents, isWholePurchaseUnit } from '@gutterinv/shared';
import { Card } from '../components/ui/card.js';
import { Badge } from '../components/ui/badge.js';
import { Button } from '../components/ui/button.js';
import { Input } from '../components/ui/input.js';
import { Skeleton } from '../components/ui/skeleton.js';
import { Sheet, SheetContent } from '../components/ui/sheet.js';
import { type TabItem } from '../components/ui/tabs.js';
import { cn } from '../lib/utils.js';
import { formatCents } from '../lib/format.js';
import { qk } from '../lib/queryKeys.js';
import { useToast } from '../components/Toast.js';
import { describeBulkActiveResult } from '../lib/bulk-active.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { statusConfirmCopy, applyActiveOptimistic } from '../lib/sku-status.js';
import {
  categoryMatchesProductLine,
  type ProductLine,
} from '../lib/product-line.js';
import {
  catalogColumnsForCategory,
  unifiedSortAccessor,
  unifiedFilterAccessor,
  sortKeyLabel,
  sortKeyTitle,
  uomStatusDisplay,
  type CatalogColumn,
} from './inventory-table-config.js';

type FilterMode = 'all' | 'low' | 'common' | 'disabled';

const FILTERS: ReadonlyArray<{ value: FilterMode; label: string }> = [
  { value: 'all', label: 'Active' },
  { value: 'low', label: 'Low stock' },
  { value: 'common', label: 'Common' },
  { value: 'disabled', label: 'Disabled' },
];

// The main list shows ONE column group at a time, switched by a page-level tab.
// Catalog = per-category attribute columns; Operational = stock; Commercial =
// cost/price/margin. (Description never shows on the page — it lives in the panel.)
type ViewMode = 'catalog' | 'operational' | 'commercial';

const VIEW_TABS: ReadonlyArray<TabItem<ViewMode>> = [
  { value: 'catalog', label: 'Catalog' },
  { value: 'operational', label: 'Stock' },
  { value: 'commercial', label: 'Cost/Sell Price' },
];

// A few columns whose header is the binding width (header wider than their values)
// AND whose displayed label == this key. The `.eyebrow` header renders uppercase
// at 11px with 0.08em letter-spacing + a sort caret — all wider than the plain
// 14px measureText estimate below — so under % normalization they'd clip. Nudge
// their measured width up by these px so the full header (and, for Material, the
// "Aluminum" value) sits comfortably without spilling into the next column.
// (Keyed by widthCols `header`; tune freely — purely cosmetic spacing.)
const HEADER_BUMP_PX: Record<string, number> = {
  Purchase: 16, Consume: 16, Cost: 16, Factor: 16, Status: 14, Material: 18,
};

// A FILTERABLE column header must fit [sort-arrow + funnel] (+ a sliver of label):
// floor its measured width so it never normalizes below this. The Card minWidth (=
// Σ colPx, post-floor) then scrolls the table horizontally instead of squeezing the
// funnel off the edge. Empirically clean at ≥52px (pl-2/pr-2); 56 leaves breathing room.
const FILTER_COL_MIN_PX = 56;

// Stock-view quantity columns render a fixed-width value span (w-12) + unit span (w-6)
// — Reorder a w-[4.5rem] input + unit — which are WIDER than their measured text. Pin
// them to explicit widths in colPx so the unit label (e.g. "FT") clears the next column
// and the header + sort caret aren't clipped.
const STOCK_QTY_COLS = new Set(['On hand', 'Committed', 'Free', 'Reorder at']);

// Measure the EXACT rendered pixel width of text for column sizing (canvas, not a
// per-char estimate — so columns fit their content without trailing gaps). Cell
// text is ~14px Inter; SKU codes are monospace (the `ticker` class).
let _measureCtx: CanvasRenderingContext2D | null = null;
function measureTextPx(text: string, mono = false): number {
  if (typeof document === 'undefined') return text.length * 8;
  if (!_measureCtx) _measureCtx = document.createElement('canvas').getContext('2d');
  if (!_measureCtx) return text.length * 8;
  _measureCtx.font = mono
    ? '500 14px ui-monospace, SFMono-Regular, Menlo, monospace'
    : '500 14px Inter, system-ui, sans-serif';
  return _measureCtx.measureText(text).width;
}

export function InventoryPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchText, setSearchText] = useState('');
  const [filter, setFilter] = useState<FilterMode>('all');
  // Sort + per-column faceted filters are lifted HERE (not inside InventoryTable)
  // so they're SHARED across the Catalog / Operational / Commercial views and the
  // per-category Catalog tables — and so the toolbar can show clearable chips for
  // them even on a view that doesn't contain the sorted/filtered column.
  const [sort, setSort] = useState<SortState<string>>({ key: null, dir: 'asc' });
  const onSortChange = useCallback((next: SortState<string>) => setSort(next), []);
  const [colFilters, setColFilters] = useState<Record<string, Set<string>>>({});
  // Product line is durable in the URL (?line=gutter|insulation) so a refresh
  // keeps the active line (feedback_refresh_preserves_view).
  const productLine: ProductLine =
    searchParams.get('line') === 'insulation' ? 'insulation' : 'gutter';
  const setProductLine = useCallback(
    (line: ProductLine) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('line', line);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  // Active column-group view (Catalog / Operational / Commercial), durable in the
  // URL (?view=) so a refresh keeps it. Non-cost roles can't see Commercial.
  const rawView = searchParams.get('view');
  const view: ViewMode =
    rawView === 'operational' || rawView === 'commercial' ? rawView : 'catalog';
  const setView = useCallback(
    (next: ViewMode) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set('view', next);
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [newSkuOpen, setNewSkuOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Deep-link scroll-to-row state. `highlightId` = the SKU whose row should be
  // scrolled-to + flashed; `pendingDetailRef` is the synchronous handoff to the
  // collapse-init effect (so it keeps that category expanded despite the
  // all-collapsed default); `scrolledForRef` guards single-scroll-per-target;
  // `fallbackOpenRef` opens the drawer even if the row never mounts.
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const scrolledForRef = useRef<string | null>(null);
  const fallbackOpenRef = useRef<number | null>(null);
  const pendingDetailRef = useRef<string | null>(null);

  const toast = useToast();
  // Pending status-change awaiting confirmation. Both the bulk bar and the
  // detail-panel toggle funnel through this → the ConfirmDialog below.
  const [statusChange, setStatusChange] = useState<{
    ids: string[];
    active: boolean;
    count: number;
    label?: string;
  } | null>(null);
  const requestStatusChange = useCallback(
    (ids: string[], active: boolean, label?: string) => {
      if (ids.length === 0) return;
      setStatusChange({ ids, active, count: ids.length, label });
    },
    [],
  );

  const bulkActiveMutation = useMutation({
    // ONE request per ≤500-id batch (the client chunks above the server's cap),
    // each an atomic UPDATE. NEVER fails silently — success + error both toast.
    mutationFn: ({ ids, active }: { ids: string[]; active: boolean }) =>
      bulkPatchSkusActive(ids, active),
    // Optimistic: flip the rows in the inventory cache up front so the status
    // badges + Active/Disabled filter counts update instantly (both derive from
    // this list); roll back on error.
    onMutate: async ({ ids, active }) => {
      await queryClient.cancelQueries({ queryKey: qk.inventory.skusAll() });
      const prev = queryClient.getQueryData<InventoryListResponse>(qk.inventory.skusAll());
      if (prev) {
        queryClient.setQueryData<InventoryListResponse>(
          qk.inventory.skusAll(),
          applyActiveOptimistic(prev, ids, active),
        );
      }
      return { prev };
    },
    onSuccess: ({ updated }, { active }) => {
      setSelectedIds(new Set());
      toast.success(describeBulkActiveResult(updated, active));
    },
    onError: (err, _vars, ctx) => {
      // Roll back the optimistic flip, then surface the reason. A
      // BulkActiveBatchError names which batch failed + how many already
      // succeeded; onSettled re-fetches so any partial success reconciles.
      if (ctx?.prev) queryClient.setQueryData(qk.inventory.skusAll(), ctx.prev);
      toast.error(err instanceof Error ? err.message : 'Bulk update failed');
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
      void queryClient.invalidateQueries({ queryKey: ['skus'] });
    },
  });

  // ?detail=<skuId> is the DURABLE source of truth for the open SKU drawer — a
  // refresh re-opens it (feedback_refresh_preserves_view) and the Notifications
  // bell's Low-Inventory rows deep-link via it. Captured once at first render so
  // the selectedId→URL sync effect (which clears ?detail while selectedId is null
  // on the first commit) can't race the deep-link open below.
  const initialDetailRef = useRef(searchParams.get('detail'));
  const detailHandledRef = useRef(false);

  const { data: me } = useQuery({ queryKey: qk.me(), queryFn: getMe });
  // Cost + inventory actions (New SKU / Upload vendor prices / Receive delivery,
  // and every COST display) are open to ALL THREE web roles — Inventory is
  // intentionally NOT role-based (back_office included). Crew use the bot, not
  // this dashboard. ONE predicate — see the server mirror in inventory.ts
  // (`canSeeCost`). NOTE: this is INVENTORY-only; job/estimate cost basis and the
  // /financials dashboard stay owner/owner+admin gated.
  const canSeeCost =
    me?.role === 'owner' || me?.role === 'admin' || me?.role === 'back_office';
  const canManageInventory = canSeeCost;
  // Managing categories (markup, rename, reorder, delete) is open to every web
  // role (owner / admin / back_office) — the category routes gate on auth only,
  // not role. Field crew use the bot, not this dashboard, so these three are the
  // full set.
  const canManageCategories =
    me?.role === 'owner' || me?.role === 'admin' || me?.role === 'back_office';
  // Reorder threshold is operational inventory, not financial — editable by the
  // same three web roles (owner / admin / back_office), same as cost (see
  // canSeeCost above — Inventory is non-role-based).
  const canEditInventory =
    me?.role === 'owner' || me?.role === 'admin' || me?.role === 'back_office';

  const { data, isLoading, error } = useQuery({
    // Distinct key (skusAll) — this is the ONLY query that pulls disabled SKUs.
    // Keeping it off the shared `skus()` key prevents it polluting the Estimate
    // picker's active-only cache. Invalidations of `skus()` still reach this via
    // prefix match.
    queryKey: qk.inventory.skusAll(),
    queryFn: () => getInventorySkus({ includeInactive: true }),
  });

  // Catalog fields (color, size, price override) live on the
  // raw Sku — the inventory rollup endpoint doesn't carry them. Join
  // client-side by sku id; both queries are cheap and cached.
  // includeInactive so DISABLED SKUs are in skuLookup → skuDetail is non-null → the commercial
  // list shows (and edits) their cost/price like active rows. Distinct key (allWithInactive)
  // keeps inactive SKUs out of the active-only caches the Estimate picker reads.
  // NB: the ['skus'] invalidations below refresh this via React-Query PREFIX match — load-bearing.
  const { data: skuCatalog } = useQuery({
    queryKey: qk.skus.allWithInactive(),
    queryFn: () => getSkus({ includeInactive: true }),
  });
  const skuLookup = useMemo(() => {
    const m = new Map<string, Sku>();
    if (skuCatalog) for (const s of skuCatalog) m.set(s.id, s);
    return m;
  }, [skuCatalog]);

  // Wait for `data` to land so we can verify the requested SKU exists before
  // acting (an unknown id would render an empty sheet). Instead of opening the
  // drawer immediately, we set `highlightId` so the matched row's callback ref
  // can scroll-then-open (the drawer scroll-locks <body>, so we must scroll
  // first). A fallback timer opens the drawer anyway if the row never mounts
  // (filtered out / can't expand). Once consumed, drop the search param.
  // On the first data load, honor an incoming ?detail deep-link: scroll-then-open
  // (the drawer scroll-locks <body>, so we scroll first). Runs ONCE off the
  // captured initial param — NOT off live searchParams — so the sync effect below
  // can own the URL without re-triggering this. The param is NOT stripped: it is
  // now durable, kept in sync with selectedId.
  useEffect(() => {
    if (detailHandledRef.current) return;
    const detailId = initialDetailRef.current;
    if (!detailId || !data) return;
    detailHandledRef.current = true;
    if (!data.skus.some((s) => s.skuId === detailId)) return;
    // Synchronous handoff to collapse-init (runs later in this commit) so the
    // target's category starts expanded.
    pendingDetailRef.current = detailId;
    scrolledForRef.current = null;
    setHighlightId(detailId);
    if (fallbackOpenRef.current) window.clearTimeout(fallbackOpenRef.current);
    fallbackOpenRef.current = window.setTimeout(() => {
      if (scrolledForRef.current !== detailId) {
        setSelectedId(detailId);
        setHighlightId(null);
      }
    }, 250);
  }, [data]);

  // Mirror the open SKU to ?detail (durable). Leaves ?tab untouched (the panel
  // owns it) and ?line untouched. Runs with selectedId=null on the first commit
  // and clears ?detail — harmless because initialDetailRef already captured the
  // deep-link value for the effect above.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (selectedId) next.set('detail', selectedId);
        else next.delete('detail');
        return next;
      },
      { replace: true },
    );
  }, [selectedId, setSearchParams]);

  // Belt-and-suspenders for the already-mounted case (e.g. the topbar bell
  // deep-links while we're already on /inventory, so collapse-init won't
  // re-run): expand the target's category so its row mounts.
  useEffect(() => {
    if (!highlightId || !data) return;
    const t = data.skus.find((s) => s.skuId === highlightId);
    if (!t) return;
    setCollapsedCategories((prev) => {
      if (!prev.has(t.categoryId)) return prev;
      const next = new Set(prev);
      next.delete(t.categoryId);
      return next;
    });
  }, [highlightId, data]);

  // Scroll the matched row into view (centered) THEN open the drawer, fired by
  // the row's callback ref once it's in the DOM (after its category expands).
  const highlightRowRef = useCallback(
    (el: HTMLTableRowElement | null) => {
      if (!el || !highlightId || scrolledForRef.current === highlightId) return;
      scrolledForRef.current = highlightId;
      if (fallbackOpenRef.current) window.clearTimeout(fallbackOpenRef.current);
      el.scrollIntoView({ block: 'center', behavior: 'instant' as ScrollBehavior });
      setSelectedId(highlightId);
      window.setTimeout(() => setHighlightId(null), 2500);
    },
    [highlightId],
  );

  // Fetch the full category list separately so empty (newly-created)
  // categories still render a section in the table — the SKU list alone
  // can't surface a category that has no SKUs yet.
  const { data: allCategories } = useQuery({
    queryKey: ['sku-categories'],
    queryFn: getSkuCategories,
  });
  const categoryMap = useMemo(() => {
    const m = new Map<string, SkuCategory>();
    if (allCategories) for (const c of allCategories) m.set(c.id, c);
    return m;
  }, [allCategories]);

  // Product-line scope (Gutter / Insulation). Categories carry no product-line
  // column — matched by name (see lib/product-line.ts). Every status filter,
  // badge count, and the grouped table all narrow to this base so the numbers
  // match the visible rows.
  const productLineSkus = useMemo(
    () =>
      data?.skus.filter((s) =>
        categoryMatchesProductLine(s.categoryName, productLine),
      ) ?? [],
    [data, productLine],
  );

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = searchText.trim().toLowerCase();
    return productLineSkus.filter((s) => {
      // Active/inactive gate: 'disabled' shows ONLY inactive rows; every
      // other filter shows ONLY active rows. The endpoint returns both
      // (includeInactive=true), so we partition client-side.
      if (filter === 'disabled') {
        if (s.active) return false;
      } else {
        if (!s.active) return false;
      }
      if (filter === 'low' && !s.isLowStock) return false;
      if (filter === 'common' && !s.isCommon) return false;
      if (!q) return true;
      return (
        s.code.toLowerCase().includes(q) ||
        s.name.toLowerCase().includes(q) ||
        (s.variant?.toLowerCase().includes(q) ?? false) ||
        (s.partNo?.toLowerCase().includes(q) ?? false) ||
        s.categoryName.toLowerCase().includes(q)
      );
    });
  }, [data, productLineSkus, searchText, filter]);

  // Counts derived from the product-line-scoped base so the badges + the
  // "X of Y SKUs" header track what's actually visible for the active line.
  const activeCount = useMemo(
    () => productLineSkus.filter((s) => s.active).length,
    [productLineSkus],
  );
  const lowStockCount = useMemo(
    () => productLineSkus.filter((s) => s.active && s.isLowStock).length,
    [productLineSkus],
  );
  const commonCount = useMemo(
    () => productLineSkus.filter((s) => s.active && s.isCommon).length,
    [productLineSkus],
  );
  const disabledCount = useMemo(
    () => productLineSkus.filter((s) => !s.active).length,
    [productLineSkus],
  );

  // Lean list: ONE table over the whole (product-line-scoped, filtered) set,
  // grouped by category inside InventoryTable. categoryFilter scopes which
  // category SECTIONS appear (so empty categories of the active line still show
  // when no filter/search is narrowing). useCallback keeps the grouping memo
  // stable across renders.
  // ALL views render ONE table PER CATEGORY (each shows that category's own column
  // set; Commercial's set is uniform across categories). `showEmptyCategories` keeps
  // empty categories visible when no filter/search is narrowing.
  const showEmptyCategories = filter === 'all' && searchText.trim().length === 0;

  // The active product line's categories, in displayOrder — drives the order of
  // the per-category Catalog tables.
  const orderedLineCategories = useMemo(
    () =>
      [...(allCategories ?? [])]
        .filter((c) => categoryMatchesProductLine(c.name, productLine))
        .sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name)),
    [allCategories, productLine],
  );

  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);

  // Default to ALL categories collapsed on first load. Once initialised
  // we don't auto-collapse again — the user's manual expand/collapse
  // wins for the rest of the session.
  const collapseInitialised = useRef(false);
  useEffect(() => {
    if (collapseInitialised.current) return;
    if (!data || !allCategories) return;
    const ids = new Set<string>();
    for (const c of allCategories) ids.add(c.id);
    for (const s of data.skus) ids.add(s.categoryId);
    // Keep a deep-link target's category EXPANDED through the all-collapsed
    // default. `pendingDetailRef` is set synchronously by the ?detail effect
    // (which runs earlier in this commit, before it strips the param), so it's
    // readable here regardless of whether skus or categories resolved first.
    const pending = pendingDetailRef.current;
    if (pending) {
      const t = data.skus.find((s) => s.skuId === pending);
      if (t) ids.delete(t.categoryId);
    }
    setCollapsedCategories(ids);
    collapseInitialised.current = true;
  }, [data, allCategories]);

  const toggleCategoryCollapse = useCallback((catId: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) {
        next.delete(catId);
      } else {
        next.add(catId);
      }
      return next;
    });
  }, []);

  const selected = data?.skus.find((s) => s.skuId === selectedId) ?? null;

  if (isLoading) {
    return <InventorySkeleton />;
  }

  if (isBlockingError(error, !!data)) {
    return (
      <Card className="border-destructive p-6">
        <div className="font-semibold text-destructive">Failed to load inventory</div>
        <div className="mt-2 text-sm text-muted-foreground">
          {error instanceof Error ? error.message : String(error)}
        </div>
      </Card>
    );
  }

  // Non-cost roles can't see the Commercial view → drop the tab + fall back.
  const visibleViewTabs = VIEW_TABS.filter((t) => canSeeCost || t.value !== 'commercial');
  const effectiveView: ViewMode =
    view === 'commercial' && !canSeeCost ? 'catalog' : view;

  // Props shared by every InventoryTable instance (rows / categoryFilter /
  // attributeSet passed at the call site). The Operational view edits reorder; the
  // Commercial view edits cost/price (needs the full Sku via skuLookup).
  const sharedTableProps = {
    view: effectiveView,
    canSeeCost,
    canEditInventory,
    canManageCategories,
    categoryMap,
    skuLookup,
    collapsedCategories,
    toggleCategoryCollapse,
    selectedIds,
    setSelectedIds,
    highlightId,
    highlightRowRef,
    setSelectedId,
    allCategories,
    filter,
    searchText,
    sort,
    onSortChange,
    colFilters,
    setColFilters,
  };

  // Active sort/filter chips — visible + clearable so the user is never stuck in an
  // ordering/filter they can't see. The sort + column-filter chips show even on a
  // view that doesn't contain the column (e.g. "Sorted by: Cost" while on Catalog).
  type Chip = { key: string; label: string; clear: () => void };
  const activeChips: Chip[] = [];
  if (sort.key) {
    activeChips.push({
      key: 'sort',
      label: `Sorted by: ${sortKeyLabel(sort.key)} ${sort.dir === 'asc' ? '↑' : '↓'}`,
      clear: () => setSort({ key: null, dir: 'asc' }),
    });
  }
  if (filter !== 'all') {
    activeChips.push({
      key: 'filter',
      label: `Filtered by: ${FILTERS.find((f) => f.value === filter)?.label ?? filter}`,
      clear: () => setFilter('all'),
    });
  }
  if (searchText.trim()) {
    activeChips.push({
      key: 'search',
      label: `Search: "${searchText.trim()}"`,
      clear: () => setSearchText(''),
    });
  }
  for (const [label, set] of Object.entries(colFilters)) {
    if (set && set.size > 0) {
      activeChips.push({
        key: `col:${label}`,
        label: `${label}: ${set.size === 1 ? [...set][0] : `${set.size} selected`}`,
        clear: () =>
          setColFilters((p) => {
            const n = { ...p };
            delete n[label];
            return n;
          }),
      });
    }
  }
  const clearAllChips = () => {
    setSort({ key: null, dir: 'asc' });
    setFilter('all');
    setSearchText('');
    setColFilters({});
  };

  return (
    <div className="pt-6 space-y-5">
      <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <p className="text-sm text-muted-foreground">
                <span className="tnum inline-block w-9 text-right">{filtered.length}</span>
                {' of '}
                <span className="tnum inline-block w-9 text-right">{activeCount}</span>
                {' SKUs'}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {FILTERS.map((f) => {
                  const active = filter === f.value;
                  const badge =
                    f.value === 'all'
                      ? activeCount
                      : f.value === 'low'
                        ? lowStockCount
                        : f.value === 'common'
                          ? commonCount
                          : f.value === 'disabled'
                            ? disabledCount
                            : undefined;
                  return (
                    <button
                      key={f.value}
                      onClick={() => setFilter(f.value)}
                      className={cn(
                        'h-8 rounded-md border px-3 text-[0.8125rem] font-medium transition-all duration-150',
                        // Pop-out on hover (lift + navy shadow), press back down on click.
                        'transform-gpu hover:-translate-y-0.5 hover:shadow-[0_4px_12px_-2px_rgba(27,42,63,0.18)] active:translate-y-0 active:shadow-sm',
                        active
                          ? 'border-primary bg-accent text-primary'
                          : 'border-border bg-card text-muted-foreground hover:bg-accent/40',
                      )}
                    >
                      {f.label}
                      {badge !== undefined && badge > 0 && (
                        <span
                          className={cn(
                            'ml-1.5 rounded-sm px-1 text-[0.6875rem]',
                            f.value === 'low'
                              ? 'bg-warning/20 text-warning-foreground'
                              : active
                                ? 'bg-primary/10 text-primary'
                                : 'bg-secondary text-taupe',
                          )}
                        >
                          {badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            {canManageCategories && (
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => setCategoryManagerOpen(true)}>
                  Manage categories
                </Button>
                {canManageInventory && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => setNewSkuOpen(true)}>
                      <Plus className="h-4 w-4" />
                      New SKU
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setCsvOpen(true)}>
                      <Upload className="h-4 w-4" />
                      Upload vendor prices
                    </Button>
                    <Button size="sm" onClick={() => setReceiveOpen(true)}>
                      <PackagePlus className="h-4 w-4" />
                      Receive delivery
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="relative w-[27rem]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-taupe" />
            <Input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search SKU code, name, variant, category..."
              className="h-9 pl-9"
            />
          </div>

          {/* Product line (Gutter/Insulation) + the column-group view buttons on ONE
              row — both are matching h-8 (32px) segmented button groups (same styling
              + aria-pressed behavior). items-center keeps them aligned; flex-wrap lets
              them drop to two rows on very narrow screens instead of overflowing. */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap gap-1.5">
              {(['gutter', 'insulation'] as const).map((line) => {
                const active = productLine === line;
                return (
                  <button
                    key={line}
                    onClick={() => setProductLine(line)}
                    aria-pressed={active}
                    className={cn(
                      'h-8 rounded-md border px-3 text-[0.8125rem] font-medium capitalize transition-all duration-150',
                      // Pop-out on hover (lift + navy shadow), press back down on click.
                      'transform-gpu hover:-translate-y-0.5 hover:shadow-[0_4px_12px_-2px_rgba(27,42,63,0.18)] active:translate-y-0 active:shadow-sm',
                      active
                        ? 'border-primary bg-accent text-primary'
                        : 'border-border bg-card text-muted-foreground hover:bg-accent/40',
                    )}
                  >
                    {line}
                  </button>
                );
              })}
            </div>

            <div className="h-6 w-px bg-border" />

            {/* Column-group view: Catalog (per-category attributes) / Operational
                (stock) / Commercial (cost·price·margin). Durable in ?view. Same
                segmented-button pattern as the Gutter/Insulation toggle above. */}
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Inventory column view">
              {visibleViewTabs.map((tab) => {
                const active = effectiveView === tab.value;
                return (
                  <button
                    key={tab.value}
                    onClick={() => setView(tab.value)}
                    aria-pressed={active}
                    className={cn(
                      'h-8 rounded-md border px-3 text-[0.8125rem] font-medium capitalize transition-all duration-150',
                      // Pop-out on hover (lift + navy shadow), press back down on click.
                      'transform-gpu hover:-translate-y-0.5 hover:shadow-[0_4px_12px_-2px_rgba(27,42,63,0.18)] active:translate-y-0 active:shadow-sm',
                      active
                        ? 'border-primary bg-accent text-primary'
                        : 'border-border bg-card text-muted-foreground hover:bg-accent/40',
                    )}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Active sort/filter chips — each clears its own state; "Clear all" when
              more than one is active. Shared across all three views. */}
          {activeChips.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {activeChips.map((chip) => (
                <span
                  key={chip.key}
                  className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-xs text-muted-foreground"
                >
                  {chip.label}
                  <button
                    type="button"
                    onClick={chip.clear}
                    aria-label={`Clear ${chip.label}`}
                    className="rounded-sm p-0.5 text-taupe transition-colors hover:bg-accent/60 hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              {activeChips.length > 1 && (
                <button
                  type="button"
                  onClick={clearAllChips}
                  className="text-xs text-primary underline-offset-2 hover:underline"
                >
                  Clear all
                </button>
              )}
            </div>
          )}

          {selectedIds.size > 0 && (
            <div className="flex items-center gap-3 rounded-md border border-primary/40 bg-accent/50 px-3 py-2">
              <span className="text-sm font-semibold text-primary">
                {selectedIds.size} selected
              </span>
              <Button
                size="sm"
                onClick={() => requestStatusChange([...selectedIds], filter === 'disabled')}
                disabled={bulkActiveMutation.isPending}
                variant={filter === 'disabled' ? 'default' : 'destructive'}
              >
                {filter === 'disabled' ? 'Enable' : 'Disable'} selected
              </Button>
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                className="ml-auto text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
              >
                Clear selection
              </button>
            </div>
          )}

          {/* ALL three views render ONE bare table PER category (each with its own
              colgroup), in displayOrder, stacked inside a single Card so collapsed
              categories sit flush. Each table's caret sits above its headers, in a
              per-table sticky <thead> that hands off seamlessly to the next category.
              Catalog passes per-category attribute columns; Commercial uses the shared
              uniform columns (Cost · Price · Margin · Factor · Status) for every
              category. A single shared table could not repeat the column header at each
              category boundary — the bug this fixes. */}
          <Card className="overflow-clip p-0">
            {orderedLineCategories.map((cat) => {
              const catRows = filtered.filter((r) => r.categoryId === cat.id);
              if (catRows.length === 0 && !showEmptyCategories) return null;
              return (
                <InventoryTable
                  key={cat.id}
                  rows={catRows}
                  bare
                  catalogColumns={
                    effectiveView === 'catalog'
                      ? catalogColumnsForCategory(cat.name)
                      : undefined
                  }
                  categoryFilter={(name) => name === cat.name}
                  {...sharedTableProps}
                />
              );
            })}
            {filtered.length === 0 && (
              <div className="p-12 text-center">
                <div className="text-sm font-medium text-muted-foreground">No SKUs match</div>
                <div className="text-xs text-taupe">Try clearing the search or filter</div>
              </div>
            )}
          </Card>

          <Sheet open={selectedId !== null} onOpenChange={(o) => !o && setSelectedId(null)}>
            {/* Wide enough to hold the two-column edit hub (Stock | Pricing) side by
                side; the cards themselves switch to two columns via a container query
                on the panel width (see .edit-hub-* in index.css), not a viewport breakpoint. */}
            <SheetContent className="w-[920px] max-w-[96vw] sm:max-w-[920px]">
              {selected && (
                <InventoryDetailPanel
                  sku={selected}
                  canSeeCost={canSeeCost}
                  canManageCategories={canManageCategories}
                  canEditReorder={canEditInventory}
                  onToggleActive={(s) =>
                    requestStatusChange([s.skuId], !s.active, `${s.code} – ${s.name}`)
                  }
                />
              )}
            </SheetContent>
          </Sheet>

          {/* Status-change confirmation — single (panel) + bulk (selection bar). */}
          <ConfirmDialog
            open={statusChange !== null}
            {...(statusChange
              ? statusConfirmCopy({
                  active: statusChange.active,
                  count: statusChange.count,
                  label: statusChange.label,
                })
              : { title: '' })}
            isPending={bulkActiveMutation.isPending}
            onConfirm={() => {
              if (!statusChange) return;
              bulkActiveMutation.mutate({ ids: statusChange.ids, active: statusChange.active });
              setStatusChange(null);
            }}
            onCancel={() => setStatusChange(null)}
          />

          {canManageCategories && (
            <CategoryManagerDialog
              open={categoryManagerOpen}
              onOpenChange={setCategoryManagerOpen}
            />
          )}
          {canManageInventory && (
            <>
              {/* Parent SKUs are abstract profiles — never received/stocked. */}
              <ReceiveDeliveryDialog
                open={receiveOpen}
                onOpenChange={setReceiveOpen}
                skus={(data?.skus ?? []).filter((s) => !s.isParent)}
                categories={allCategories ?? []}
              />
              <NewSkuDialog open={newSkuOpen} onOpenChange={setNewSkuOpen} />
              <Dialog open={csvOpen} onOpenChange={setCsvOpen}>
                <DialogContent className="sm:max-w-3xl">
                  <DialogHeader>
                    <DialogTitle>Upload vendor prices</DialogTitle>
                    <DialogDescription>
                      Bulk update SKU costs from a vendor CSV. Markup % is
                      applied on top of the new cost automatically.
                    </DialogDescription>
                  </DialogHeader>
                  <CsvImport />
                </DialogContent>
              </Dialog>
            </>
          )}
    </div>
  );
}

/**
 * The single lean inventory table for the active product line. Owns its own sort
 * (SKU/Description) + category grouping + select-all. Rows show SKU · Description ·
 * Key spec · Status; the per-category attributes + stock/cost/price live in the
 * detail panel. `categoryFilter` scopes which category sections appear (the active
 * product line).
 */
function InventoryTable({
  rows,
  view,
  catalogColumns,
  bare,
  canSeeCost,
  canEditInventory,
  categoryFilter,
  canManageCategories,
  categoryMap,
  skuLookup,
  collapsedCategories,
  toggleCategoryCollapse,
  selectedIds,
  setSelectedIds,
  highlightId,
  highlightRowRef,
  setSelectedId,
  allCategories,
  filter,
  searchText,
  sort,
  onSortChange,
  colFilters,
  setColFilters,
}: {
  rows: InventorySkuRow[];
  /** Active column-group view (Catalog attrs / Operational stock / Commercial). */
  view: ViewMode;
  /** Catalog view: the per-category columns (after SKU · Description). Empty for a
   *  catch-all category (then Catalog shows just SKU · Description). One table per
   *  category. */
  catalogColumns?: CatalogColumn[];
  /** Render WITHOUT the Card wrapper (Catalog stacks per-category tables inside
   *  one Card so collapsed categories sit flush — no inter-table gap). */
  bare?: boolean;
  canSeeCost: boolean;
  canEditInventory: boolean;
  /** Which categories this table renders sections for (scopes empty-category rows). */
  categoryFilter: (name: string) => boolean;
  canManageCategories: boolean;
  categoryMap: Map<string, SkuCategory>;
  skuLookup: Map<string, Sku>;
  collapsedCategories: Set<string>;
  toggleCategoryCollapse: (catId: string) => void;
  selectedIds: Set<string>;
  setSelectedIds: Dispatch<SetStateAction<Set<string>>>;
  highlightId: string | null;
  highlightRowRef: (el: HTMLTableRowElement | null) => void;
  setSelectedId: Dispatch<SetStateAction<string | null>>;
  allCategories: SkuCategory[] | undefined;
  filter: FilterMode;
  searchText: string;
  /** Shared sort state (lifted to InventoryPage) — controlled here. */
  sort: SortState<string>;
  onSortChange: (next: SortState<string>) => void;
  /** Shared per-column faceted filters (lifted), keyed by column label. */
  colFilters: Record<string, Set<string>>;
  setColFilters: Dispatch<SetStateAction<Record<string, Set<string>>>>;
}) {
  const catCols = catalogColumns ?? [];
  // Re-measure column widths once webfonts finish loading (canvas measureText is
  // off until Inter is ready), so columns don't size against the fallback font.
  const [fontsReady, setFontsReady] = useState(false);
  useEffect(() => {
    let alive = true;
    document.fonts?.ready.then(() => {
      if (alive) setFontsReady(true);
    });
    return () => {
      alive = false;
    };
  }, []);
  // Sort uses the SHARED, view-independent `unifiedSortAccessor` (see
  // inventory-table-config) so rows order identically across views; the sort STATE
  // is controlled from InventoryPage via the `sort` / `onSortChange` props.

  // Per-column faceted filters: EVERY catalog attribute column (all categories) +
  // the categorical Operational columns. Keyed by column label; empty = no filter.
  const filterCols = useMemo<{ label: string; accessor: (s: InventorySkuRow) => unknown }[]>(() => {
    if (view === 'catalog') {
      return catCols.map((c) => ({
        label: c.label,
        accessor: (s: InventorySkuRow) => {
          const v = c.render(s);
          return typeof v === 'string' || typeof v === 'number' ? v : null;
        },
      }));
    }
    if (view === 'commercial') {
      return [
        // Canonical facet options — must match unifiedFilterAccessor's normalized
        // value, or selecting "BOX" matches no rows that store "BX".
        { label: 'Purchase', accessor: (s: InventorySkuRow) => normalizeUom(s.purchaseUom) },
        { label: 'Consume', accessor: (s: InventorySkuRow) => normalizeUom(s.consumptionUom) },
        // Dropdown options = the 3-state display label (OK / Needs / —), matching
        // unifiedFilterAccessor so a selected facet matches the rows it lists.
        { label: 'UoM status', accessor: (s: InventorySkuRow) => uomStatusDisplay(s.uomStatus).label },
      ];
    }
    return [];
  }, [view, catCols]);
  // `colFilters` is now a SHARED prop (lifted to InventoryPage). filterCols /
  // filterOptions stay per-view — they only drive the dropdown UI (colFilterFor).
  const filterOptions = useMemo(
    () => Object.fromEntries(filterCols.map((fc) => [fc.label, distinctValues(rows, fc.accessor)])),
    [rows, filterCols],
  );
  // Filtering applies the SHARED colFilters via the view-independent
  // unifiedFilterAccessor, so a filter set in one view still filters in the others.
  const visibleRows = useMemo(() => {
    if (!anyActive(colFilters)) return rows;
    const accessors = Object.fromEntries(
      Object.keys(colFilters).map((label) => [
        label,
        (r: InventorySkuRow) => unifiedFilterAccessor(r, label),
      ]),
    );
    return rows.filter((r) => rowPasses(r, colFilters, accessors));
  }, [rows, colFilters]);
  const colFilterFor = (label: string) =>
    filterCols.some((fc) => fc.label === label) ? (
      <ColumnFilter
        label={label}
        options={filterOptions[label] ?? []}
        selected={colFilters[label] ?? new Set<string>()}
        onChange={(next) => setColFilters((p) => ({ ...p, [label]: next }))}
      />
    ) : null;

  // FIXED column widths, computed ONCE from the FULL row set (not the filtered
  // view) so filtering can never resize columns. Widths are the MEASURED pixel
  // width of each column's widest real value (canvas measureText — not a per-char
  // estimate), so a column is exactly as wide as its content + padding and short
  // values don't leave big trailing gaps. A trailing filler col soaks up the
  // leftover so the table still spans the page (collapsed rows stay full-width).
  // MUST mirror the rendered column order: checkbox · SKU · Description · <view> · Status.
  const widthCols = useMemo(() => {
    const u = (s: InventorySkuRow) => unitLabel(s.unit);
    const n = (v: number) => v.toLocaleString();
    type WC = { header: string; valueOf: (s: InventorySkuRow) => string; mono?: boolean; filter?: boolean };
    const list: WC[] = [
      { header: 'SKU', valueOf: (s) => s.code, mono: true },
      { header: 'Description', valueOf: (s) => s.name + (s.variant ? ` · ${s.variant}` : '') },
    ];
    if (view === 'catalog') {
      for (const c of catCols) {
        list.push({
          header: c.label,
          filter: true, // every catalog attribute column is filterable
          valueOf: (s) => {
            const v = c.render(s);
            return typeof v === 'string' || typeof v === 'number' ? String(v) : '—';
          },
        });
      }
    } else if (view === 'operational') {
      list.push(
        { header: 'On hand', valueOf: (s) => `${n(s.onHand)} ${u(s)}` },
        { header: 'Committed', valueOf: (s) => `${n(s.committedToQuotes)} ${u(s)}` },
        { header: 'Free', valueOf: (s) => `${n(s.effectivelyFree)} ${u(s)}` },
        { header: 'Reorder at', valueOf: (s) => `${n(s.reorderThreshold)} ${u(s)}` },
      );
    } else {
      // Paired order: each unit sits beside its money (BUY+COST, SELL+Sell Price),
      // then Factor · UoM · Margin. BUY/SELL/Factor/UoM relocated here from Operational.
      list.push(
        { header: 'Purchase', filter: true, valueOf: (s) => normalizeUom(s.purchaseUom) || '—' },
        { header: 'Cost', valueOf: () => '$0,000.00' },
        { header: 'Consume', filter: true, valueOf: (s) => normalizeUom(s.consumptionUom) || '—' },
        { header: 'Price', valueOf: () => '$0,000.00' },
        { header: 'Factor', valueOf: (s) => (s.conversionFactor != null ? String(s.conversionFactor) : '—') },
        { header: 'UoM status', filter: true, valueOf: (s) => uomStatusDisplay(s.uomStatus).label },
        { header: 'Margin', valueOf: () => '000.0%' },
      );
    }
    list.push({ header: 'Status', valueOf: (s) => (!s.active ? 'Disabled' : s.isCommon ? 'Common' : '') });
    return list;
  }, [view, catCols]);

  // Measured pixel width of each column's widest value — used ONLY to PROPORTION
  // the columns. We never emit absolute widths (that overflows the container);
  // instead they're normalized to percentages of 100% in the colgroup, so the
  // table-fixed w-full table always fits its width no matter how many columns a
  // category has. The widest text column absorbs the most space.
  const colPx = useMemo(() => {
    // Stock / Cost-Sell-Price use a TIGHT, content-pinned layout (Description flexes);
    // Catalog keeps the generous proportional layout.
    const tight = view === 'operational' || view === 'commercial';
    const widths = [34]; // checkbox — small floor
    for (const wc of widthCols) {
      const headerPx = measureTextPx(wc.header, false);
      if (wc.header === 'Description') {
        // Description is the FLEXIBLE column in Stock / Cost-Sell-Price (auto width in
        // the colgroup → absorbs ALL leftover), so this px is only its minWidth floor.
        // In Catalog it's the proportional column width.
        const ws = rows.map((r) => measureTextPx(wc.valueOf(r), false)).sort((a, b) => a - b);
        const pct = ws.length ? ws[Math.floor(ws.length * 0.65)] : headerPx;
        widths.push(Math.max(pct, headerPx) + 24 + 12);
        continue;
      }
      // Widest rendered content for this column (header vs values).
      let contentPx = headerPx;
      for (const r of rows) {
        const w = measureTextPx(wc.valueOf(r), wc.mono);
        if (w > contentPx) contentPx = w;
      }
      if (tight) {
        // Pin each short/numeric column to a TIGHT content width (~60–90px; money
        // columns set the floor so "$0,000.00" never clips) so the pinned block stays
        // compact and Description (the flexible col) absorbs the rest. SKU is capped
        // (110–130px) so long monospace codes don't bloat it to ~200px.
        if (wc.header === 'SKU') {
          // Wide enough for the longest codes (e.g. GG56HOOVERDAMGRYBLK) on one line,
          // clamped to ~190–210px so it never clips and never bloats.
          widths.push(Math.min(Math.max(Math.round(contentPx) + 24, 190), 210));
        } else if (STOCK_QTY_COLS.has(wc.header)) {
          // Fixed value+unit spans are wider than the measured text — pin wide enough
          // that the unit clears the next column and the header + caret aren't clipped.
          widths.push(wc.header === 'Reorder at' ? 128 : 120);
        } else {
          widths.push(Math.max(Math.round(contentPx) + 24 + (wc.filter ? 14 : 4), 60));
        }
        continue;
      }
      // Catalog (unchanged): generous content + chrome + filter floor + header bump.
      const w = contentPx + 24 + (wc.filter ? 18 : 12) + (wc.mono ? 20 : 0) + (HEADER_BUMP_PX[wc.header] ?? 0);
      widths.push(wc.filter ? Math.max(w, FILTER_COL_MIN_PX) : w);
    }
    return widths;
  }, [widthCols, rows, fontsReady, view]);
  const totalPx = colPx.reduce((a, b) => a + b, 0) || 1;
  // Full-span colSpan (category/parent/empty rows) = the rendered column count.
  // colPx mirrors checkbox + SKU + Description + <visible view cols> + Status, so
  // its length IS the column count — and it auto-tracks the Operational hide toggle.
  const colCount = colPx.length;
  // Stock / Cost-Sell-Price use an explicit fixed-px colgroup where Description is the
  // ONLY flexible column: its width is calc(100% − Σfixed), so the table sums to exactly
  // the container width and Description takes only the LEFTOVER (ellipsizes) instead of
  // expanding to its content and blowing the table past the container (the auto-col bug).
  const descColIdx = widthCols.findIndex((w) => w.header === 'Description') + 1;
  const fixedSumPx = colPx.reduce((sum, px, i) => (i === descColIdx ? sum : sum + px), 0);

  // Controlled: sort state is the shared `sort` prop; the view-independent
  // unifiedSortAccessor makes the order identical across views. (Don't re-destructure
  // `sort` — it's already the prop in scope.)
  const { sorted, toggle } = useSortable(visibleRows, unifiedSortAccessor, { sort, onSortChange });

  // Parent SKUs (abstract coil profiles) render as collapsible sub-headers that
  // group their color children; collapsing one hides its children. With the
  // default (null) sort the API's code order places each parent above its variants.
  const [collapsedParents, setCollapsedParents] = useState<Set<string>>(new Set());
  const toggleParent = useCallback((skuId: string) => {
    setCollapsedParents((prev) => {
      const next = new Set(prev);
      if (next.has(skuId)) next.delete(skuId);
      else next.add(skuId);
      return next;
    });
  }, []);
  // Parents start COLLAPSED on first load so a category opens to a tidy list of profile
  // headers, not every color. Seeded once; the user's expand/collapse then wins. Carve-out:
  // keep a deep-linked child's parent EXPANDED so the highlighted row isn't hidden.
  const parentCollapseInit = useRef(false);
  useEffect(() => {
    if (parentCollapseInit.current || rows.length === 0) return;
    const ids = new Set(rows.filter((r) => r.isParent).map((r) => r.skuId));
    if (highlightId) {
      const target = rows.find((r) => r.skuId === highlightId);
      if (target?.parentSkuId) ids.delete(target.parentSkuId);
    }
    setCollapsedParents(ids);
    parentCollapseInit.current = true;
  }, [rows, highlightId]);
  // Bulk select-all operates on SELECTABLE rows only — parents have no checkbox.
  const selectableSorted = useMemo(() => sorted.filter((s) => !s.isParent), [sorted]);

  // Group sorted rows by category. Within each category the sort order is
  // preserved (sort runs before grouping). When the full category list is
  // available, walk it by displayOrder so empty/new categories still render a
  // section; when filter is 'all' AND search is empty, keep empty categories
  // visible. `categoryFilter` scopes the sections to the active product line.
  const grouped = useMemo(() => {
    const rowsByCategory = new Map<string, InventorySkuRow[]>();
    const nameByCategory = new Map<string, string>();
    for (const s of sorted) {
      const arr = rowsByCategory.get(s.categoryId);
      if (arr) arr.push(s);
      else rowsByCategory.set(s.categoryId, [s]);
      nameByCategory.set(s.categoryId, s.categoryName);
    }

    if (allCategories && allCategories.length > 0) {
      const showEmpty = filter === 'all' && searchText.trim().length === 0;
      const ordered = [...allCategories]
        .filter((cat) => categoryFilter(cat.name))
        .sort(
          (a, b) =>
            a.displayOrder - b.displayOrder || a.name.localeCompare(b.name),
        );
      return ordered.flatMap((cat) => {
        const rows = rowsByCategory.get(cat.id) ?? [];
        if (rows.length === 0 && !showEmpty) return [];
        return [{ categoryId: cat.id, categoryName: cat.name, rows }];
      });
    }

    return Array.from(rowsByCategory.entries()).map(([id, rows]) => ({
      categoryId: id,
      categoryName: nameByCategory.get(id) ?? '',
      rows,
    }));
  }, [sorted, allCategories, filter, searchText, categoryFilter]);

  const headerCells = (
    <>
      <th className="pl-3 pr-2 py-3" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selectableSorted.length > 0 && selectableSorted.every((s) => selectedIds.has(s.skuId))}
          ref={(el) => {
            if (el) {
              const visSel = selectableSorted.filter((s) => selectedIds.has(s.skuId)).length;
              el.indeterminate = visSel > 0 && visSel < selectableSorted.length;
            }
          }}
          onChange={() => {
            setSelectedIds((prev) => {
              const next = new Set(prev);
              const allSelected =
                selectableSorted.length > 0 && selectableSorted.every((s) => next.has(s.skuId));
              if (allSelected) for (const s of selectableSorted) next.delete(s.skuId);
              else for (const s of selectableSorted) next.add(s.skuId);
              return next;
            });
          }}
          aria-label="Select all visible"
          className="h-4 w-4 cursor-pointer accent-primary"
        />
      </th>
      <SortHeader label={sortKeyLabel('code')} title={sortKeyTitle('code')} active={sort.key === 'code'} dir={sort.dir} onClick={() => toggle('code')} className="overflow-hidden" />
      <SortHeader label={sortKeyLabel('Description')} title={sortKeyTitle('Description')} active={sort.key === 'Description'} dir={sort.dir} onClick={() => toggle('Description')} className="overflow-hidden" />
      {view === 'catalog' ? (
        catCols.map((col) => (
          <SortHeader
            key={col.label}
            label={col.label}
            title={col.title}
            active={sort.key === col.label}
            dir={sort.dir}
            onClick={() => toggle(col.label)}
            align={col.numeric ? 'right' : 'left'}
            className="overflow-hidden"
            filter={colFilterFor(col.label)}
          />
        ))
      ) : view === 'operational' ? (
        // Display labels + tooltips come from the config single-source
        // (sortKeyLabel/sortKeyTitle) — the same sortKeyLabel the "Sorted by" chip
        // uses, so header and chip can't drift. The sort/filter KEYS (toggle/
        // sort.key/colFilterFor) keep the original full identifier strings.
        <>
          <SortHeader label={sortKeyLabel('On hand')} title={sortKeyTitle('On hand')} active={sort.key === 'On hand'} dir={sort.dir} onClick={() => toggle('On hand')} align="right" className="overflow-hidden" />
          <SortHeader label={sortKeyLabel('Committed')} title={sortKeyTitle('Committed')} active={sort.key === 'Committed'} dir={sort.dir} onClick={() => toggle('Committed')} align="right" className="overflow-hidden" />
          <SortHeader label={sortKeyLabel('Free')} title={sortKeyTitle('Free')} active={sort.key === 'Free'} dir={sort.dir} onClick={() => toggle('Free')} align="right" className="overflow-hidden" />
          <SortHeader label={sortKeyLabel('Reorder at')} title={sortKeyTitle('Reorder at')} active={sort.key === 'Reorder at'} dir={sort.dir} onClick={() => toggle('Reorder at')} align="right" className="overflow-hidden" />
        </>
      ) : (
        <>
          <SortHeader label={sortKeyLabel('Purchase')} title={sortKeyTitle('Purchase')} active={sort.key === 'Purchase'} dir={sort.dir} onClick={() => toggle('Purchase')} align="center" className="overflow-hidden" filter={colFilterFor('Purchase')} />
          <SortHeader label={sortKeyLabel('Cost')} title={sortKeyTitle('Cost')} active={sort.key === 'Cost'} dir={sort.dir} onClick={() => toggle('Cost')} align="right" className="overflow-hidden" />
          <SortHeader label={sortKeyLabel('Consume')} title={sortKeyTitle('Consume')} active={sort.key === 'Consume'} dir={sort.dir} onClick={() => toggle('Consume')} align="center" className="overflow-hidden" filter={colFilterFor('Consume')} />
          <th className="px-3 py-3 text-right"><span className="eyebrow flex min-h-[1rem] w-full items-center justify-end whitespace-nowrap">Sell Price</span></th>
          <SortHeader label={sortKeyLabel('Factor')} title={sortKeyTitle('Factor')} active={sort.key === 'Factor'} dir={sort.dir} onClick={() => toggle('Factor')} align="right" className="overflow-hidden" />
          <SortHeader label={sortKeyLabel('UoM status')} title={sortKeyTitle('UoM status')} active={sort.key === 'UoM status'} dir={sort.dir} onClick={() => toggle('UoM status')} align="center" className="overflow-hidden" filter={colFilterFor('UoM status')} />
          <th className="px-3 py-3 text-right"><span className="eyebrow flex min-h-[1rem] w-full items-center justify-end whitespace-nowrap">Margin</span></th>
        </>
      )}
      <th className="px-3 py-3 text-right">
        <span className="eyebrow flex min-h-[1rem] w-full items-center justify-end">Status</span>
      </th>
    </>
  );

  const inner = (
    <>
      {/* The Operational "More columns" toggle lives at the PAGE level now (one for
          the whole view, not one per per-category table). */}
      {/* No overflow wrapper here: the colgroup normalizes widths to 100% so the
          table ALWAYS fits its container — horizontal scroll is never needed. A
          scroll/overflow wrapper here would also trap the sticky header against a
          non-vertically-scrolling box (see the sticky <thead> below), so it stays a
          plain block and sticky resolves up to <main class="overflow-auto">. */}
      <div>
        {/* table-fixed w-full + colgroup with PERCENTAGE widths normalized to 100%
            (proportioned by measured content, computed from the full row set so
            filtering can't resize them). Percentages — never absolute widths — so
            the table always fits its container regardless of column count, and the
            widest text column (Description) gets the most room. */}
        <table className="w-full table-fixed text-left">
          <colgroup>
            {colPx.map((px, i) => {
              // Stock / Cost-Sell-Price: pin every column to its measured content width
              // (px) EXCEPT Description, which gets calc(100% − Σfixed) — so the table
              // sums to exactly 100% of the container and Description consumes only the
              // LEFTOVER (ellipsizes), never expanding to content and blowing the table
              // past the container. Catalog keeps the proportional % (its attribute text
              // columns legitimately differ).
              const fixedLayout = view === 'operational' || view === 'commercial';
              if (fixedLayout) {
                return i === descColIdx ? (
                  <col key={i} style={{ width: `calc(100% - ${Math.round(fixedSumPx)}px)` }} />
                ) : (
                  <col key={i} style={{ width: `${Math.round(px)}px` }} />
                );
              }
              return <col key={i} style={{ width: `${((px / totalPx) * 100).toFixed(3)}%` }} />;
            })}
          </colgroup>
          {/* No leading shared <thead> in any view: each category renders its OWN
              sticky <thead> (caret + column header as ONE unit) in the group map below,
              so the column header repeats at every category boundary. Sticky resolves
              against <main class="overflow-auto"> (the real vertical scroller) — the
              Card uses overflow-clip (clips corners but is NOT a scroll container) and
              the table wrapper has no overflow. */}
          {grouped.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={colCount} className="px-4 py-12 text-center">
                  <div className="text-sm font-medium text-muted-foreground">
                    No SKUs match
                  </div>
                  <div className="text-xs text-taupe">
                    Try clearing the search or filter
                  </div>
                </td>
              </tr>
            </tbody>
          ) : (
            grouped.map((group) => {
              const collapsed = collapsedCategories.has(group.categoryId);
              // Caret cell (category name · count · markup) — identical in both modes.
              const caretCell = (
                <td
                  colSpan={colCount}
                  className="cursor-pointer px-2 py-2 transition-colors hover:bg-secondary/60"
                  onClick={() => toggleCategoryCollapse(group.categoryId)}
                >
                  <div className="flex items-center gap-1.5">
                    <ChevronDown
                      className={cn(
                        'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
                        collapsed ? '-rotate-90' : '',
                      )}
                    />
                    <span className="text-[0.8125rem] font-semibold text-foreground">
                      {group.categoryName}
                    </span>
                    <span className="text-xs text-taupe">({group.rows.length})</span>
                    <CategoryMarkupEditor
                      categoryId={group.categoryId}
                      defaultMarkupPct={categoryMap.get(group.categoryId)?.defaultMarkupPct ?? 0}
                      canEdit={canManageCategories}
                    />
                  </div>
                </td>
              );
              // Data rows (empty-state + parent/InventoryRow) — identical in both modes.
              const dataRows = !collapsed && (
                <>
                  {group.rows.length === 0 && (
                    <tr>
                      <td colSpan={colCount} className="px-4 py-3 text-center text-xs text-taupe">
                        No SKUs in this category yet — add one with “New SKU”.
                      </td>
                    </tr>
                  )}
                  {group.rows
                    // Hide a parent's color children when that parent is collapsed.
                    .filter((sku) => !(sku.parentSkuId && collapsedParents.has(sku.parentSkuId)))
                    .map((sku) =>
                      sku.isParent ? (
                        <ParentRow
                          key={sku.skuId}
                          sku={sku}
                          colCount={colCount}
                          collapsed={collapsedParents.has(sku.skuId)}
                          childCount={group.rows.filter((r) => r.parentSkuId === sku.skuId).length}
                          onToggle={() => toggleParent(sku.skuId)}
                        />
                      ) : (
                        <InventoryRow
                          key={sku.skuId}
                          sku={sku}
                          view={view}
                          catalogColumns={catCols}
                          skuDetail={skuLookup.get(sku.skuId) ?? null}
                          categoryDefaultMarkup={categoryMap.get(sku.categoryId)?.defaultMarkupPct ?? 0}
                          canSeeCost={canSeeCost}
                          canEditReorder={canEditInventory}
                          canToggleCommon={canManageCategories}
                          highlighted={sku.skuId === highlightId}
                          rowRef={sku.skuId === highlightId ? highlightRowRef : undefined}
                          onClick={() => setSelectedId(sku.skuId)}
                          isSelected={selectedIds.has(sku.skuId)}
                          onToggleSelect={() =>
                            setSelectedIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(sku.skuId)) next.delete(sku.skuId);
                              else next.add(sku.skuId);
                              return next;
                            })
                          }
                        />
                      ),
                    )}
                </>
              );

              // ALL views: ONE category per table (the page renders a bare table per
              // category). The caret + column-header rows live in a SINGLE sticky
              // <thead> (one unit, top-0) so it hands off seamlessly to the next flush
              // table — the outgoing thead is pushed out exactly as the incoming pins,
              // no offset gap, no cross. Collapsed → the thead is caret-only and still
              // pins through its short table. Data rows go in the sibling <tbody>.
              return (
                <Fragment key={group.categoryId}>
                  <thead className="sticky top-0 z-20">
                    <tr className="border-b border-border bg-secondary">{caretCell}</tr>
                    {!collapsed && (
                      <tr className="border-b border-border bg-background shadow-[0_1px_0_0_hsl(var(--border))] [&>th]:bg-background">
                        {headerCells}
                      </tr>
                    )}
                  </thead>
                  <tbody>{dataRows}</tbody>
                </Fragment>
              );
            })
          )}
        </table>
      </div>
    </>
  );
  // All three views stack per-category tables inside ONE Card (bare) so collapsed
  // categories read as a continuous list — the page supplies that Card (see
  // InventoryPage). overflow-CLIP (not overflow-hidden): still clips the table to the
  // Card's rounded corners, but `clip` is not a scroll container, so the per-category
  // sticky <thead> resolves past it to <main> instead of pinning to this non-scrolling
  // Card.
  //
  // The non-bare branch below is a standalone fallback (no current caller passes it):
  // minWidth = Σ colPx (post-floor) so on a narrow window <main overflow-auto> scrolls
  // HORIZONTALLY and filterable columns hold their FILTER_COL_MIN_PX floor instead of
  // squeezing the funnel off the edge; on a wide window it's inert.
  return bare ? (
    inner
  ) : (
    <Card className="overflow-clip p-0" style={{ minWidth: `${Math.ceil(totalPx)}px` }}>
      {inner}
    </Card>
  );
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/**
 * A parent SKU (abstract coil profile, e.g. C113427 = 11¾"×.027 aluminum) rendered
 * as a collapsible sub-header that groups its color children. Parents are never
 * stocked or selectable, so this row carries NO checkbox, stock cells, cost, or
 * actions — just the profile identity, a "not stocked" badge, and a collapse caret.
 * Under the default code sort each parent sits immediately above its variants.
 */
function ParentRow({
  sku,
  colCount,
  collapsed,
  childCount,
  onToggle,
}: {
  sku: InventorySkuRow;
  colCount: number;
  collapsed: boolean;
  childCount: number;
  onToggle: () => void;
}) {
  return (
    <tr className="border-b border-border bg-secondary/20">
      <td
        colSpan={colCount}
        className="cursor-pointer px-2 py-2 transition-colors hover:bg-secondary/40"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
              collapsed ? '-rotate-90' : '',
            )}
          />
          <span className="ticker text-sm">{sku.code}</span>
          <span className="truncate text-sm text-muted-foreground">{sku.name}</span>
          <Badge variant="secondary" className="shrink-0 text-[0.6875rem] font-medium">
            Parent · not stocked
          </Badge>
          <span className="shrink-0 text-xs text-taupe">
            {childCount} {childCount === 1 ? 'color' : 'colors'}
          </span>
        </div>
      </td>
    </tr>
  );
}

function InventoryRow({
  sku,
  view,
  catalogColumns,
  skuDetail,
  categoryDefaultMarkup,
  canSeeCost,
  canEditReorder,
  canToggleCommon,
  highlighted,
  rowRef,
  onClick,
  isSelected,
  onToggleSelect,
}: {
  sku: InventorySkuRow;
  view: ViewMode;
  /** Catalog view: the per-category columns this row renders (after Description). */
  catalogColumns?: CatalogColumn[];
  /** Full Sku (priceCentsOverride) — drives the Commercial view's price cell.
   *  Null while the catalog query resolves. */
  skuDetail: Sku | null;
  categoryDefaultMarkup: number;
  /** Commercial cost/price cells shown + editable for owner+admin+back_office. */
  canSeeCost: boolean;
  /** Operational Reorder cell is editable for owner/admin/back_office. */
  canEditReorder: boolean;
  /** Star toggles isCommon inline — owner/admin/back_office. View-only roles
   *  see a read-only star (filled when common). */
  canToggleCommon: boolean;
  /** Deep-link flash: brief background highlight when arrived-at via ?detail=. */
  highlighted?: boolean;
  /** Callback ref on the <tr> for the deep-link scroll-into-view (only the
   *  matched row receives it). */
  rowRef?: (el: HTMLTableRowElement | null) => void;
  onClick: () => void;
  /** When provided, renders a leftmost checkbox cell for multi-select. */
  isSelected?: boolean;
  onToggleSelect?: () => void;
}) {
  const queryClient = useQueryClient();
  const catCols = catalogColumns ?? [];
  // Hover-prefetch the panel's history + commitments so opening the drawer is instant.
  const prefetchSkuDetail = () => {
    void queryClient.prefetchQuery({
      queryKey: qk.inventory.txns(sku.skuId),
      queryFn: () => getInventoryTxns(sku.skuId, 50),
      staleTime: 10_000,
    });
    void queryClient.prefetchQuery({
      queryKey: ['inventory', 'commitments', sku.skuId],
      queryFn: () => getSkuCommitments(sku.skuId),
      staleTime: 10_000,
    });
  };

  // --- Editable Cost + Price cells (Commercial view) ---
  const [costValue, setCostValue] = useState(
    skuDetail ? (skuDetail.costCents / 100).toFixed(2) : '',
  );
  const [costState, setCostState] = useState<SaveState>('idle');
  const costRef = useRef<HTMLInputElement>(null);
  const priceHasOverride = skuDetail?.priceCentsOverride != null;
  const [priceValue, setPriceValue] = useState(
    priceHasOverride ? (skuDetail!.priceCentsOverride! / 100).toFixed(2) : '',
  );
  const [priceState, setPriceState] = useState<SaveState>('idle');
  const priceRef = useRef<HTMLInputElement>(null);
  // --- Editable Reorder cell (Operational view) ---
  const [reorderValue, setReorderValue] = useState(String(sku.reorderThreshold));
  const [reorderState, setReorderState] = useState<SaveState>('idle');
  const reorderRef = useRef<HTMLInputElement>(null);
  // --- Editable conversion Factor (Operational view) ---
  const [factorValue, setFactorValue] = useState(
    sku.conversionFactor != null ? String(sku.conversionFactor) : '',
  );
  const [factorState, setFactorState] = useState<SaveState>('idle');
  const factorRef = useRef<HTMLInputElement>(null);

  // Resync local editor state when the upstream row reloads.
  useEffect(() => {
    if (!skuDetail) return;
    setCostValue((skuDetail.costCents / 100).toFixed(2));
    setPriceValue(
      skuDetail.priceCentsOverride != null
        ? (skuDetail.priceCentsOverride / 100).toFixed(2)
        : '',
    );
  }, [skuDetail, skuDetail?.costCents, skuDetail?.priceCentsOverride]);
  useEffect(() => {
    setFactorValue(sku.conversionFactor != null ? String(sku.conversionFactor) : '');
  }, [sku.conversionFactor]);
  useEffect(() => {
    setReorderValue(String(sku.reorderThreshold));
  }, [sku.reorderThreshold]);

  // Shared cost→price math: divide cost (per purchaseUom) by conversionFactor to get
  // the price per consumptionUom, then apply markup. Whole-bag insulation (coverageSf
  // > 0) is NOT divided. Identical to the worker engine + the detail panel.
  const pricing = skuDetail
    ? computeSellPriceCents(
        {
          costCents: skuDetail.costCents,
          conversionFactor: skuDetail.conversionFactor,
          isWholeUnitPurchase: isWholePurchaseUnit(skuDetail),
        },
        categoryDefaultMarkup,
      )
    : null;
  const computedPriceCents = pricing ? pricing.sellPriceCents : 0;
  const effectivePriceCents = priceHasOverride
    ? skuDetail!.priceCentsOverride!
    : computedPriceCents;
  // Margin uses cost expressed per consumption unit (same unit as the price); using
  // raw costCents (per purchase unit) would make factor≠1 SKUs show negative margin.
  const marginPct =
    pricing && effectivePriceCents > 0
      ? ((effectivePriceCents - pricing.costPerConsumptionCents) / effectivePriceCents) * 100
      : null;

  const costMutation = useMutation({
    mutationFn: (val: number) => patchSku(sku.skuId, { costCents: val }),
    onSuccess: () => {
      setCostState('saved');
      void queryClient.invalidateQueries({ queryKey: ['skus'] });
      void queryClient.invalidateQueries({ queryKey: qk.inventory.skus() });
      setTimeout(() => setCostState('idle'), 1200);
    },
    onError: () => {
      setCostState('error');
      setTimeout(() => setCostState('idle'), 2000);
    },
  });
  const priceMutation = useMutation({
    mutationFn: (val: number | null) => patchSku(sku.skuId, { priceCentsOverride: val }),
    onSuccess: () => {
      setPriceState('saved');
      void queryClient.invalidateQueries({ queryKey: ['skus'] });
      setTimeout(() => setPriceState('idle'), 1200);
    },
    onError: () => {
      setPriceState('error');
      setTimeout(() => setPriceState('idle'), 2000);
    },
  });
  const reorderMutation = useMutation({
    mutationFn: (val: number) => patchSku(sku.skuId, { reorderThreshold: val }),
    onSuccess: () => {
      setReorderState('saved');
      void queryClient.invalidateQueries({ queryKey: ['skus'] });
      void queryClient.invalidateQueries({ queryKey: qk.inventory.skus() });
      void queryClient.invalidateQueries({ queryKey: qk.dashboard.today() });
      setTimeout(() => setReorderState('idle'), 1200);
    },
    onError: () => {
      setReorderState('error');
      setTimeout(() => setReorderState('idle'), 2000);
    },
  });
  // Editing the factor flips conversionFactorSource → 'override' (the per-SKU
  // supplier value), so the master import won't clobber it.
  const factorMutation = useMutation({
    mutationFn: (val: number) =>
      patchSku(sku.skuId, { conversionFactor: val, conversionFactorSource: 'override' }),
    onSuccess: () => {
      setFactorState('saved');
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
      void queryClient.invalidateQueries({ queryKey: ['skus'] });
      setTimeout(() => setFactorState('idle'), 1200);
    },
    onError: () => {
      setFactorState('error');
      setTimeout(() => setFactorState('idle'), 2000);
    },
  });

  // Common star toggles isCommon. Optimistic (not a per-click rollup refetch) —
  // curating "Common" is a bulk many-clicks task and a full ['inventory']
  // refetch per click would hammer the pooler. We flip the row in the skusAll
  // cache immediately, roll back on error, and reconcile once on settle.
  const isCommonMutation = useMutation({
    mutationFn: (next: boolean) => patchSku(sku.skuId, { isCommon: next }),
    onMutate: async (next: boolean) => {
      await queryClient.cancelQueries({ queryKey: qk.inventory.skusAll() });
      const prev = queryClient.getQueryData<InventoryListResponse>(
        qk.inventory.skusAll(),
      );
      if (prev) {
        queryClient.setQueryData<InventoryListResponse>(qk.inventory.skusAll(), {
          ...prev,
          skus: prev.skus.map((row) =>
            row.skuId === sku.skuId ? { ...row, isCommon: next } : row,
          ),
        });
      }
      return { prev };
    },
    onError: (_err, _next, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(qk.inventory.skusAll(), ctx.prev);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
      void queryClient.invalidateQueries({ queryKey: ['skus'] });
    },
  });

  const saveCost = useCallback(() => {
    if (!skuDetail) return;
    const trimmed = costValue.trim();
    if (trimmed === '') {
      setCostValue((skuDetail.costCents / 100).toFixed(2));
      return;
    }
    const parsed = Math.round(Number(trimmed) * 100);
    if (Number.isNaN(parsed) || parsed < 0) {
      setCostState('error');
      setTimeout(() => setCostState('idle'), 2000);
      return;
    }
    if (parsed === skuDetail.costCents) return;
    setCostState('saving');
    costMutation.mutate(parsed);
  }, [costValue, skuDetail, costMutation]);

  const savePrice = useCallback(() => {
    const trimmed = priceValue.trim();
    if (trimmed === '') {
      if (priceHasOverride) {
        setPriceState('saving');
        priceMutation.mutate(null);
      }
      return;
    }
    const parsed = Math.round(Number(trimmed) * 100);
    if (Number.isNaN(parsed) || parsed < 0) {
      setPriceState('error');
      setTimeout(() => setPriceState('idle'), 2000);
      return;
    }
    if (parsed === skuDetail?.priceCentsOverride) return;
    setPriceState('saving');
    priceMutation.mutate(parsed);
  }, [priceValue, priceHasOverride, skuDetail?.priceCentsOverride, priceMutation]);

  const saveReorder = useCallback(() => {
    const trimmed = reorderValue.trim();
    if (trimmed === '') {
      setReorderValue(String(sku.reorderThreshold));
      return;
    }
    const parsed = Number(trimmed);
    if (Number.isNaN(parsed) || parsed < 0) {
      setReorderState('error');
      setTimeout(() => setReorderState('idle'), 2000);
      return;
    }
    if (parsed === sku.reorderThreshold) return;
    setReorderState('saving');
    reorderMutation.mutate(parsed);
  }, [reorderValue, sku.reorderThreshold, reorderMutation]);

  const saveFactor = useCallback(() => {
    const trimmed = factorValue.trim();
    if (trimmed === '') {
      setFactorValue(sku.conversionFactor != null ? String(sku.conversionFactor) : '');
      return;
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setFactorState('error');
      setTimeout(() => setFactorState('idle'), 2000);
      return;
    }
    if (parsed === sku.conversionFactor) return;
    setFactorState('saving');
    factorMutation.mutate(parsed);
  }, [factorValue, sku.conversionFactor, factorMutation]);

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  const showCheckbox = isSelected !== undefined && onToggleSelect !== undefined;

  return (
    <tr
      ref={rowRef}
      onClick={onClick}
      onMouseEnter={prefetchSkuDetail}
      className={cn(
        'cursor-pointer border-b border-border last:border-b-0 transition-colors',
        // Deep-link flash wins over every other bg (top-level branch) so the
        // arrived-at row is unmistakable; transition-colors fades it out when
        // `highlighted` clears. bg-only (no ring — transition-colors doesn't
        // animate box-shadow, so a ring would snap).
        highlighted
          ? 'bg-primary/15'
          : isSelected
            ? 'bg-primary/[0.08]'
            : !sku.active
              ? 'bg-muted/30 opacity-60 hover:bg-muted/50'
              : sku.isLowStock
                ? 'bg-warning/5 hover:bg-warning/15'
                : 'hover:bg-primary/[0.08]',
      )}
    >
      {showCheckbox && (
        <td className="pl-3 pr-2 py-3 align-middle" onClick={stop}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            className="h-4 w-4 cursor-pointer accent-primary"
            aria-label={`Select ${sku.code}`}
          />
        </td>
      )}
      <td className="px-3 py-3 align-middle overflow-hidden">
        <div className="flex items-center gap-1.5">
          {canToggleCommon ? (
            <button
              type="button"
              onClick={(e) => {
                stop(e);
                isCommonMutation.mutate(!sku.isCommon);
              }}
              disabled={isCommonMutation.isPending}
              aria-pressed={sku.isCommon}
              aria-label={
                sku.isCommon ? 'Remove from common' : 'Mark as common'
              }
              title={sku.isCommon ? 'Remove from common' : 'Mark as common'}
              className="shrink-0 rounded p-0.5 transition-colors hover:bg-secondary disabled:opacity-50"
            >
              <Star
                className={cn(
                  'h-3.5 w-3.5',
                  sku.isCommon
                    ? 'fill-warning text-warning'
                    : 'text-muted-foreground',
                )}
              />
            </button>
          ) : (
            <Star
              className={cn(
                'h-3 w-3 shrink-0 fill-warning text-warning',
                !sku.isCommon && 'invisible',
              )}
              aria-label={sku.isCommon ? 'Common SKU' : undefined}
              aria-hidden={!sku.isCommon}
            />
          )}
          <span className="ticker inline-block whitespace-nowrap text-left text-sm">
            {sku.code}
          </span>
        </div>
      </td>

      {/* Shared Description leader on every view. In Stock / Cost-Sell-Price it's the
          flexible colgroup column (absorbs leftover width), so it NEVER wraps —
          truncates with an ellipsis only if a name exceeds even that generous width.
          Catalog keeps wrapping (its Description is a proportional % that can be narrow). */}
      <td className="px-3 py-3 align-middle">
        <div
          className={cn(
            'text-sm font-medium text-foreground',
            (view === 'operational' || view === 'commercial') && 'truncate',
          )}
        >
          {sku.name}
          {sku.variant && (
            <span className="font-normal text-muted-foreground"> · {sku.variant}</span>
          )}
        </div>
      </td>

      {/* ── Catalog view: the category's attribute columns. Text columns wrap (no
          nowrap) so long values like "White High Gloss" stay visible at narrow
          widths; numeric columns stay on one line. ── */}
      {view === 'catalog' &&
        catCols.map((col) => (
          <td
            key={col.label}
            className={cn(
              'px-3 py-3 align-middle text-sm text-muted-foreground',
              // overflow-hidden: a long single-word value (e.g. "Aluminum") clips at
              // its own column edge instead of spilling over the next column. Multi-
              // word values still wrap and stay fully visible.
              col.numeric ? 'tnum text-right whitespace-nowrap' : 'text-left overflow-hidden',
            )}
          >
            {col.render(sku)}
          </td>
        ))}

      {/* ── Operational view: stock levels ── */}
      {view === 'operational' && (
        <>
          <td className="px-3 py-3 align-middle text-right">
            <span className="whitespace-nowrap">
              <span className="tnum inline-block w-12 text-right text-sm font-semibold text-foreground">
                {sku.onHand.toLocaleString()}
              </span>
              <span className="ml-1 inline-block w-6 text-left text-[0.6875rem] uppercase tracking-wider text-taupe">
                {unitLabel(sku.unit)}
              </span>
            </span>
          </td>
          <td className="px-3 py-3 align-middle text-right">
            <span className="whitespace-nowrap">
              <span className="tnum inline-block w-12 text-right text-sm text-muted-foreground">
                {sku.committedToQuotes.toLocaleString()}
              </span>
              <span className="ml-1 inline-block w-6 text-left text-[0.6875rem] uppercase tracking-wider text-taupe">
                {unitLabel(sku.unit)}
              </span>
            </span>
          </td>
          <td className="px-3 py-3 align-middle text-right">
            <span className="whitespace-nowrap">
              <span
                className={cn(
                  'tnum inline-block w-12 text-right text-sm font-bold',
                  sku.effectivelyFree < 0
                    ? 'text-destructive'
                    : sku.isLowStock
                      ? 'text-warning'
                      : 'text-foreground',
                )}
              >
                {sku.effectivelyFree.toLocaleString()}
              </span>
              <span className="ml-1 inline-block w-6 text-left text-[0.6875rem] uppercase tracking-wider text-taupe">
                {unitLabel(sku.unit)}
              </span>
            </span>
          </td>
          <td
            className="px-3 py-3 align-middle text-right"
            onClick={canEditReorder ? stop : undefined}
          >
            {canEditReorder ? (
              <div className="flex items-center justify-end gap-1">
                <div className="relative">
                  <input
                    ref={reorderRef}
                    type="number"
                    min="0"
                    step="any"
                    value={reorderValue}
                    onChange={(e) => setReorderValue(e.target.value)}
                    onBlur={saveReorder}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        saveReorder();
                        reorderRef.current?.blur();
                      }
                    }}
                    className={cn(
                      'tnum h-7 w-[4.5rem] rounded border bg-background px-2 text-right text-sm outline-none transition-colors',
                      'focus:ring-2 focus:ring-ring focus:ring-offset-1',
                      reorderState === 'error'
                        ? 'border-destructive'
                        : reorderState === 'saved'
                          ? 'border-success'
                          : 'border-input text-foreground',
                    )}
                  />
                  {reorderState === 'saved' && (
                    <Check className="absolute -right-5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-success animate-in fade-in" />
                  )}
                </div>
                <span className="inline-block w-6 text-left text-[0.6875rem] uppercase tracking-wider text-taupe">
                  {unitLabel(sku.unit)}
                </span>
              </div>
            ) : (
              <span className="whitespace-nowrap">
                <span className="tnum inline-block w-12 text-right text-sm text-muted-foreground">
                  {sku.reorderThreshold.toLocaleString()}
                </span>
                <span className="ml-1 inline-block w-6 text-left text-[0.6875rem] uppercase tracking-wider text-taupe">
                  {unitLabel(sku.unit)}
                </span>
              </span>
            )}
          </td>
        </>
      )}

      {/* ── Commercial view: BUY · Cost · SELL · Sell Price · Factor · UoM · Margin ── */}
      {view === 'commercial' && (
        <>
          <td className="px-3 py-3 align-middle text-center text-sm text-muted-foreground whitespace-nowrap">
            {normalizeUom(sku.purchaseUom) || '—'}
          </td>
          <td className="px-3 py-3 align-middle text-right" onClick={stop}>
            {canSeeCost && skuDetail ? (
              <div className="flex items-center justify-end gap-0.5">
                <span className="text-xs text-muted-foreground">$</span>
                <div className="relative">
                  <input
                    ref={costRef}
                    type="number"
                    min="0"
                    step="0.01"
                    value={costValue}
                    onChange={(e) => setCostValue(e.target.value)}
                    onBlur={saveCost}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        saveCost();
                        costRef.current?.blur();
                      }
                    }}
                    className={cn(
                      'tnum no-spinner h-7 w-[5.375rem] rounded border bg-background px-2 text-right text-sm outline-none transition-colors',
                      'focus:ring-2 focus:ring-ring focus:ring-offset-1',
                      costState === 'error'
                        ? 'border-destructive'
                        : costState === 'saved'
                          ? 'border-success'
                          : 'border-input text-foreground',
                    )}
                  />
                  {costState === 'saved' && (
                    <Check className="absolute -right-5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-success animate-in fade-in" />
                  )}
                </div>
              </div>
            ) : (
              <span className="text-taupe">—</span>
            )}
          </td>
          <td className="px-3 py-3 align-middle text-center text-sm text-muted-foreground whitespace-nowrap">
            {normalizeUom(sku.consumptionUom) || '—'}
          </td>
          <td className="px-3 py-3 align-middle text-right" onClick={stop}>
            {skuDetail ? (
              <div className="flex items-center justify-end gap-1.5">
                <span className="text-xs text-muted-foreground">$</span>
                <div className="relative">
                  <input
                    ref={priceRef}
                    type="number"
                    min="0"
                    step="0.01"
                    value={priceValue}
                    placeholder={(computedPriceCents / 100).toFixed(2)}
                    onChange={(e) => setPriceValue(e.target.value)}
                    onBlur={savePrice}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        savePrice();
                        priceRef.current?.blur();
                      }
                    }}
                    title={
                      priceHasOverride
                        ? `Override set. Empty to revert to computed (${formatCents(computedPriceCents)}).`
                        : `Computed from cost × (1 + markup%). Enter a value to override.`
                    }
                    className={cn(
                      'tnum no-spinner h-7 w-[5.375rem] rounded border bg-background px-2 text-right text-sm outline-none transition-colors',
                      'focus:ring-2 focus:ring-ring focus:ring-offset-1',
                      priceState === 'error'
                        ? 'border-destructive'
                        : priceState === 'saved'
                          ? 'border-success'
                          : priceHasOverride
                            ? 'border-input text-foreground font-semibold'
                            : 'border-border text-muted-foreground',
                    )}
                  />
                  {priceState === 'saved' && (
                    <Check className="absolute -right-5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-success animate-in fade-in" />
                  )}
                </div>
                {priceHasOverride && (
                  <span
                    title="Override active — quotes use this price, ignoring cost × markup. Clear the cell to revert."
                    className="rounded-sm border border-warning/40 bg-warning/10 px-1 text-[0.625rem] font-semibold uppercase tracking-wider text-warning-foreground"
                  >
                    override
                  </span>
                )}
              </div>
            ) : (
              <span className="text-taupe">—</span>
            )}
          </td>
          {/* Factor — editable (consumption units per 1 purchase unit; the per-SKU supplier override). */}
          <td className="px-3 py-3 align-middle text-right" onClick={canEditReorder ? stop : undefined}>
            {canEditReorder ? (
              <div className="relative inline-block">
                <input
                  ref={factorRef}
                  type="number"
                  min="0"
                  step="any"
                  value={factorValue}
                  placeholder="—"
                  onChange={(e) => setFactorValue(e.target.value)}
                  onBlur={saveFactor}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      saveFactor();
                      factorRef.current?.blur();
                    }
                  }}
                  title={
                    sku.conversionFactorSource === 'override'
                      ? 'Per-SKU override.'
                      : 'Consumption units per 1 purchase unit. Edit to set a supplier override.'
                  }
                  className={cn(
                    'tnum h-7 w-[4.25rem] rounded border bg-background px-2 text-right text-sm outline-none transition-colors',
                    'focus:ring-2 focus:ring-ring focus:ring-offset-1',
                    factorState === 'error'
                      ? 'border-destructive'
                      : factorState === 'saved'
                        ? 'border-success'
                        : sku.conversionFactorSource === 'override'
                          ? 'border-input font-semibold text-foreground'
                          : 'border-input text-foreground',
                  )}
                />
                {factorState === 'saved' && (
                  <Check className="absolute -right-5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-success animate-in fade-in" />
                )}
              </div>
            ) : (
              <span className="tnum text-sm text-muted-foreground">
                {sku.conversionFactor != null ? sku.conversionFactor : '—'}
              </span>
            )}
          </td>
          <td className="px-3 py-3 align-middle text-center whitespace-nowrap">
            <UomStatusBadge status={sku.uomStatus} />
          </td>
          <td className="px-3 py-3 align-middle text-right">
            <span className="tnum text-sm font-semibold text-foreground">
              {marginPct != null ? `${marginPct.toFixed(1)}%` : '—'}
            </span>
          </td>
        </>
      )}

      <td className="px-3 py-3 align-middle text-right">
        <StatusBadges sku={sku} />
      </td>
    </tr>
  );
}

// UoM data-quality flag (migration 0071 uom_status). The six real enum values map
// (via uomStatusDisplay — single source) to three visual states: quiet OK (a subtle
// green check, no loud pill), amber "Needs" pill (action required), or a neutral dash
// for null/legacy/unknown. The literal "Review" badge — which the old code rendered
// for EVERY row because it tested non-existent 'VALIDATED'/'OK' values — is gone.
function UomStatusBadge({ status }: { status: string | null }) {
  const { state, tooltip } = uomStatusDisplay(status);
  if (state === 'ok') {
    return (
      <span title={tooltip} className="inline-flex" aria-label="OK">
        <Check className="h-3.5 w-3.5 text-success" />
      </span>
    );
  }
  if (state === 'needs') {
    return (
      <span
        title={tooltip}
        className="rounded-sm border border-warning/40 bg-warning/10 px-1 text-[0.625rem] font-semibold uppercase tracking-wider text-warning-foreground"
      >
        Needs
      </span>
    );
  }
  return (
    <span title={tooltip} className="text-taupe">
      —
    </span>
  );
}

// Status column — Disabled (inactive) takes precedence; otherwise Low and/or
// Common chips; nothing for a plain active row.
function StatusBadges({ sku }: { sku: InventorySkuRow }) {
  if (!sku.active) {
    return (
      <span className="rounded-sm border border-border bg-secondary px-1.5 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wider text-muted-foreground">
        Disabled
      </span>
    );
  }
  if (!sku.isLowStock && !sku.isCommon) {
    return <span className="text-taupe">—</span>;
  }
  return (
    <div className="flex flex-wrap items-center justify-end gap-1">
      {sku.isLowStock && <Badge variant="warning">Low</Badge>}
      {sku.isCommon && <Badge variant="secondary">Common</Badge>}
    </div>
  );
}

// Inline category-markup editor rendered on each category group-header row.
// Markup now lives ONLY at the category level (per-SKU override was removed),
// so this is the primary place to set it. Editable by every web role (owner /
// admin / back_office) since the category PATCH route is auth-gated, not
// role-gated; read-only only if canEdit is false. Saving invalidates the
// category + inventory queries so every row's computed price reflects the new
// markup immediately.
function CategoryMarkupEditor({
  categoryId,
  defaultMarkupPct,
  canEdit,
}: {
  categoryId: string;
  defaultMarkupPct: number;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState(String(defaultMarkupPct));
  const [state, setState] = useState<SaveState>('idle');
  const inputRef = useRef<HTMLInputElement>(null);

  // Resync when the upstream category row reloads (e.g. edited in the
  // Manage Categories dialog) so the inline field doesn't show a stale %.
  useEffect(() => {
    setValue(String(defaultMarkupPct));
  }, [defaultMarkupPct]);

  const mutation = useMutation({
    mutationFn: (val: number) =>
      patchSkuCategory(categoryId, { defaultMarkupPct: val }),
    onSuccess: () => {
      setState('saved');
      void queryClient.invalidateQueries({ queryKey: ['sku-categories'] });
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
      void queryClient.invalidateQueries({ queryKey: ['skus'] });
      setTimeout(() => setState('idle'), 1200);
    },
    onError: () => {
      setState('error');
      setTimeout(() => setState('idle'), 2000);
    },
  });

  const save = useCallback(() => {
    const trimmed = value.trim();
    if (trimmed === '') {
      setValue(String(defaultMarkupPct));
      return;
    }
    const parsed = Number(trimmed);
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 1000) {
      setState('error');
      setTimeout(() => setState('idle'), 2000);
      return;
    }
    if (parsed === defaultMarkupPct) return;
    setState('saving');
    mutation.mutate(parsed);
  }, [value, defaultMarkupPct, mutation]);

  if (!canEdit) {
    return (
      <span className="tnum ml-auto sticky right-2 z-10 text-xs text-muted-foreground">
        {defaultMarkupPct}% markup
      </span>
    );
  }

  return (
    <div
      className="ml-auto sticky right-2 z-10 flex items-center gap-1.5"
      onClick={(e) => e.stopPropagation()}
    >
      <span className="text-xs text-muted-foreground">Markup</span>
      <span className="text-xs text-muted-foreground">%</span>
      <div className="relative">
        <input
          ref={inputRef}
          type="number"
          min="0"
          step="1"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              save();
              inputRef.current?.blur();
            }
          }}
          className={cn(
            'tnum no-spinner h-7 w-[5.375rem] rounded border bg-background px-2 text-right text-sm outline-none transition-colors',
            'focus:ring-2 focus:ring-ring focus:ring-offset-1',
            state === 'error'
              ? 'border-destructive'
              : state === 'saved'
                ? 'border-success'
                : 'border-input text-foreground',
          )}
        />
        {state === 'saved' && (
          <Check className="absolute -right-5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-success animate-in fade-in" />
        )}
      </div>
    </div>
  );
}

function InventorySkeleton() {
  return (
    <div className="space-y-5">
      <Skeleton className="h-9 w-48" />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
      <Skeleton className="h-9 w-full max-w-md" />
      <Skeleton className="h-96 w-full" />
    </div>
  );
}
